import { useId, useState, type SVGProps } from "react";
import type { CharacterId } from "../game/core/types";
import {
  ACCESSORY_VIEW_BOX,
  CHARACTER_ACCESSORY_ANCHORS,
  CHARACTER_PREVIEW_DEFINITIONS,
  colorToCss,
  getAccessoryForCharacter,
  type AccessoryId,
  type AccessoryLayer,
  type AccessoryShape,
} from "../game/wardrobe";

export interface CharacterPreviewProps
  extends Omit<SVGProps<SVGSVGElement>, "children" | "role"> {
  readonly character: CharacterId;
  readonly accessoryId?: AccessoryId | null;
  /** Accessible description. A useful Hebrew description is generated when omitted. */
  readonly alt?: string;
  /** Use when the containing button/card already supplies the accessible name. */
  readonly decorative?: boolean;
}

function renderShape(shape: AccessoryShape, key: string) {
  const common = {
    fill: shape.fill === undefined ? "none" : colorToCss(shape.fill),
    fillOpacity: shape.opacity ?? 1,
    stroke: shape.stroke === undefined ? undefined : colorToCss(shape.stroke),
    strokeWidth: shape.strokeWidth,
    strokeLinejoin: "round" as const,
    strokeLinecap: "round" as const,
  };

  if (shape.kind === "circle") {
    return <circle key={key} {...common} cx={shape.cx} cy={shape.cy} r={shape.radius} />;
  }
  if (shape.kind === "ellipse") {
    return <ellipse key={key} {...common} cx={shape.cx} cy={shape.cy} rx={shape.radiusX} ry={shape.radiusY} />;
  }
  if (shape.kind === "rounded-rect") {
    return (
      <rect
        key={key}
        {...common}
        x={shape.x}
        y={shape.y}
        width={shape.width}
        height={shape.height}
        rx={shape.radius}
      />
    );
  }
  if (shape.kind === "polygon") {
    return <polygon key={key} {...common} points={shape.points.map(([x, y]) => `${x},${y}`).join(" ")} />;
  }

  const d = shape.commands.map((command) => {
    if (command.kind === "move") return `M ${command.x} ${command.y}`;
    if (command.kind === "line") return `L ${command.x} ${command.y}`;
    if (command.kind === "quadratic") {
      return `Q ${command.controlX} ${command.controlY} ${command.x} ${command.y}`;
    }
    if (command.kind === "cubic") {
      return `C ${command.control1X} ${command.control1Y} ${command.control2X} ${command.control2Y} ${command.x} ${command.y}`;
    }
    return "Z";
  }).join(" ");
  return <path key={key} {...common} d={d} />;
}

function renderLayer(
  shapes: readonly AccessoryShape[],
  layer: AccessoryLayer,
  anchor: { readonly x: number; readonly y: number },
) {
  return (
    <g transform={`translate(${anchor.x} ${anchor.y})`}>
      {shapes
        .filter((shape) => shape.layer === layer)
        .map((shape, index) => renderShape(shape, `${layer}-${index}`))}
    </g>
  );
}

export function CharacterPreview({
  character,
  accessoryId = null,
  alt,
  decorative = false,
  className,
  ...svgProps
}: CharacterPreviewProps) {
  const titleId = useId();
  const [failedSkinId, setFailedSkinId] = useState<AccessoryId | null>(null);
  const characterDefinition = CHARACTER_PREVIEW_DEFINITIONS[character];
  const accessory = getAccessoryForCharacter(character, accessoryId);
  const useOutfitSkin = Boolean(accessory && failedSkinId !== accessory.id);
  const anchor = accessory
    ? CHARACTER_ACCESSORY_ANCHORS[character][accessory.anchor]
    : { x: 0, y: 0 };
  const accessibleName = alt ?? (
    accessory
      ? `${characterDefinition.displayName} עם ${accessory.displayName}`
      : `${characterDefinition.displayName} ללא אביזר`
  );
  const viewBox = `${ACCESSORY_VIEW_BOX.x} ${ACCESSORY_VIEW_BOX.y} ${ACCESSORY_VIEW_BOX.width} ${ACCESSORY_VIEW_BOX.height}`;

  return (
    <svg
      {...svgProps}
      className={["character-preview", className].filter(Boolean).join(" ")}
      viewBox={viewBox}
      preserveAspectRatio="xMidYMid meet"
      role={decorative ? undefined : "img"}
      aria-hidden={decorative || undefined}
      aria-labelledby={decorative ? undefined : titleId}
      focusable="false"
    >
      {decorative ? null : <title id={titleId}>{accessibleName}</title>}
      <g aria-hidden="true">
        {useOutfitSkin && accessory ? (
          <image
            href={accessory.outfitAssetHref}
            x={-characterDefinition.outfitWidth / 2}
            y={-characterDefinition.outfitHeight / 2}
            width={characterDefinition.outfitWidth}
            height={characterDefinition.outfitHeight}
            preserveAspectRatio="xMidYMid meet"
            onError={() => setFailedSkinId(accessory.id)}
          />
        ) : (
          <>
            {renderLayer(accessory?.shapes ?? [], "back", anchor)}
            <image
              href={characterDefinition.assetHref}
              x={-characterDefinition.width / 2}
              y={-characterDefinition.height / 2}
              width={characterDefinition.width}
              height={characterDefinition.height}
              preserveAspectRatio="none"
            />
            {renderLayer(accessory?.shapes ?? [], "front", anchor)}
          </>
        )}
      </g>
    </svg>
  );
}
